// components/CommentSection/index.tsx
// フォローコメント一覧＋追加ボタンの汎用セクションコンポーネント（AttachmentSection と同じ作法）
//
// 現在の使用箇所:
//   - src/components/FollowFormModal/index.tsx（フォロー編集ダイアログ内）
//   - src/components/RelatedFollowModal/index.tsx（関連フォロータイムライン詳細内）
//   - src/components/FollowListModal/index.tsx（フォロー詳細モーダル内）

import React from 'react';
import { Button } from 'antd';
import { CommentOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { FollowComment } from '../../types/follow';

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

export interface CommentSectionProps {
  /** 表示するコメント一覧 */
  comments: FollowComment[];
  /** ログインユーザーのuserno（本人のみ編集/削除ボタンを表示。クライアント側チェックのみ） */
  currentUserNo?: number | null;
  /** 「追加」ボタンクリック時のコールバック（省略時はボタン非表示） */
  onAdd?: () => void;
  /** 編集ボタンクリック時のコールバック（省略時はボタン非表示） */
  onEdit?: (comment: FollowComment) => void;
  /** 削除ボタンクリック時のコールバック（省略時はボタン非表示） */
  onDelete?: (comment: FollowComment) => void;
  /** 見出しに件数（○件）を表示するか（0件時は常に非表示）。デフォルト: false */
  showCount?: boolean;
  /** 一覧全体に外枠ボーダーを表示するか。デフォルト: false */
  showBorder?: boolean;
  /**
   * 追加ボタンの位置。
   * 'header'（デフォルト）: 見出し行右の小さい「追加」ボタン
   * 'footer': 一覧下の大きいボタン（添付ファイル追加ボタンと同じ見た目）
   */
  addButtonStyle?: 'header' | 'footer';
  /** 見出しラベルのスタイル上書き（省略時は既存の太字/大文字/字間広めスタイルを使用） */
  labelStyle?: React.CSSProperties;
  /** 自前の見出し行（ラベル＋headerボタン）を描画しないか。外側（Form.Item label等）でタイトルを表示する場合に使用。デフォルト: false */
  hideLabel?: boolean;
}

const DEFAULT_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 'bold',
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const CommentSection: React.FC<CommentSectionProps> = ({
  comments,
  currentUserNo,
  onAdd,
  onEdit,
  onDelete,
  showCount = false,
  showBorder = false,
  addButtonStyle = 'header',
  labelStyle,
  hideLabel = false,
}) => {
  const label = showCount && comments.length > 0 ? `コメント（${comments.length}件）` : 'コメント';

  return (
    <div>
      {!hideLabel && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={labelStyle ?? DEFAULT_LABEL_STYLE}>
            {label}
          </span>
          {onAdd && addButtonStyle === 'header' && (
            <Button type="text" size="small" icon={<CommentOutlined />} onClick={onAdd}>
              追加
            </Button>
          )}
        </div>
      )}

      {comments.length > 0 && (
        <div
          style={
            showBorder
              ? { border: '1px solid #f0f0f0', borderRadius: 4, padding: 6 }
              : undefined
          }
        >
          {comments.map((c, index) => {
            const isOwn = currentUserNo != null && c.authorUserno === currentUserNo;
            return (
              <div
                key={c.followcommentno}
                style={{
                  padding: showBorder ? 6 : undefined,
                  paddingBottom: showBorder ? 6 : 10,
                  marginBottom: index < comments.length - 1 ? (showBorder ? 6 : 10) : 0,
                  borderBottom: index < comments.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 'bold', color: '#262626' }}>{c.author}</span>
                    <span style={{ fontSize: 11, color: '#bfbfbf', marginLeft: 8 }}>{c.createDate}</span>
                  </div>
                  {isOwn && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0, margin: '-5px 0' }}>
                      {onEdit && (
                        <Button
                          type="text"
                          icon={<EditOutlined style={{ fontSize: 14 }} />}
                          style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                          onClick={() => onEdit(c)}
                        />
                      )}
                      {onDelete && (
                        <Button
                          type="text"
                          icon={<DeleteOutlined style={{ fontSize: 14, color: '#ff4d4f' }} />}
                          style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                          onClick={() => onDelete(c)}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{c.text}</div>
              </div>
            );
          })}
        </div>
      )}

      {onAdd && addButtonStyle === 'footer' && (
        <Button icon={<CommentOutlined />} onClick={onAdd} style={{ marginTop: comments.length > 0 ? 8 : 0 }}>
          コメント追加
        </Button>
      )}
    </div>
  );
};

export default CommentSection;
